import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatPrice } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Heart, MessageCircle, Truck, ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import ShareRow from "@/components/ShareRow";
import { track } from "@/lib/analytics";

export default function ItemDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, favIds, toggleFavorite } = useAuth();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    setLoading(true);
    api.get(`/listings/${id}`)
      .then(({ data }) => setItem(data))
      .catch(() => setItem(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 animate-pulse">
      <div className="grid md:grid-cols-2 gap-8">
        <div className="aspect-[4/5] bg-muted rounded-2xl" />
        <div className="space-y-4">
          <div className="h-8 bg-muted rounded w-3/4" />
          <div className="h-6 bg-muted rounded w-1/3" />
          <div className="h-24 bg-muted rounded" />
        </div>
      </div>
    </div>;
  }
  if (!item) {
    return <div className="max-w-3xl mx-auto py-24 text-center">
      <div className="font-display text-2xl">{t("item.not_found")}</div>
      <Link to="/"><Button className="mt-6 rounded-full">{t("item.cta_home")}</Button></Link>
    </div>;
  }

  const isSold = item.status === "sold";
  const isOwn = user && user.id === item.seller_id;
  const isFav = favIds?.has(item.id);
  const images = item.images && item.images.length ? item.images : ["https://images.pexels.com/photos/15120548/pexels-photo-15120548.jpeg?auto=compress&cs=tinysrgb&w=940"];

  const onBuy = async () => {
    if (!user || !user.id) { navigate("/login"); return; }
    setBuying(true);
    track("payment_started", { listing_id: item.id, price: item.price, category: item.category });
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/payments/checkout", {
        listing_id: item.id, origin_url: origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || t("item.checkout_error"));
      setBuying(false);
    }
  };

  const onMessage = async () => {
    if (!user || !user.id) { navigate("/login"); return; }
    if (isOwn) { toast(t("item.own_notice")); return; }
    navigate(`/messages?with=${item.seller_id}&listing=${item.id}`);
  };

  const onFav = async () => {
    if (!user || !user.id) { navigate("/login"); return; }
    try {
      const now = await toggleFavorite(item.id);
      if (now) toast.success("Ajouté à vos favoris");
    } catch { toast.error("Erreur favoris"); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 fade-in">
      <button onClick={() => navigate(-1)} data-testid="back-btn"
        className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-6">
        <ArrowLeft className="w-4 h-4" /> {t("item.back")}
      </button>

      <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
        {/* Gallery */}
        <div>
          <div className="relative overflow-hidden rounded-2xl aspect-[4/5] bg-muted">
            <img src={images[imgIdx]} alt={item.title}
              className="w-full h-full object-cover" />
            {isSold && (
              <div className="absolute inset-0 bg-background/70 flex items-center justify-center">
                <span className="px-4 py-2 rounded-full bg-foreground text-background text-sm uppercase tracking-widest">
                  Vendu
                </span>
              </div>
            )}
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar">
              {images.map((src, i) => (
                <button key={i} onClick={() => setImgIdx(i)}
                  data-testid={`thumb-${i}`}
                  className={`shrink-0 w-20 h-24 rounded-lg overflow-hidden border-2 transition-colors ${
                    imgIdx === i ? "border-primary" : "border-transparent"
                  }`}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="overline text-muted-foreground">
            {item.category} {item.brand ? `· ${item.brand}` : ""}
          </div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl leading-tight">{item.title}</h1>
          <div className="mt-4 text-3xl font-semibold">{formatPrice(item.price)}</div>

          <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
            {item.size && <Field label={t("browse.size_placeholder")} value={`T. ${item.size}`} />}
            <Field label={t("browse.state_placeholder")} value={t(`condition.${item.condition}`, { defaultValue: item.condition })} />
            {item.brand && <Field label={t("item.field_brand")} value={item.brand} />}
            {item.color && <Field label={t("item.field_color")} value={item.color} />}
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button
              onClick={onBuy}
              disabled={isSold || isOwn || buying}
              size="lg"
              data-testid="buy-now-button"
              className="rounded-full h-12 flex-1"
            >
              {buying ? t("item.redirecting") : isSold ? t("item.sold") : isOwn ? t("item.own") : t("item.buy_now")}
            </Button>
            <Button
              onClick={onMessage}
              variant="outline"
              size="lg"
              data-testid="message-seller-button"
              className="rounded-full h-12 sm:w-auto"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              {t("item.message_seller")}
            </Button>
            <button
              onClick={onFav}
              data-testid="detail-fav-btn"
              className="hidden sm:inline-flex w-12 h-12 rounded-full border border-border hover:border-primary items-center justify-center transition-colors"
              aria-label="Favoris"
            >
              <Heart className={`w-5 h-5 ${isFav ? "fill-primary text-primary" : ""}`} />
            </button>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <Perk icon={<ShieldCheck className="w-4 h-4" />} text={t("item.perk_stripe")} />
            <Perk icon={<Truck className="w-4 h-4" />} text={t("item.perk_shipping")} />
          </div>

          {item.description && (
            <div className="mt-8">
              <div className="overline text-muted-foreground mb-2">{t("item.description")}</div>
              <p className="text-[15px] leading-relaxed whitespace-pre-line">
                {item.description}
              </p>
            </div>
          )}

          {/* Share buttons (viral) */}
          <ShareRow item={item} />

          {/* Seller card */}
          {item.seller && (
            <div className="mt-10 rounded-2xl border border-border p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center font-display text-lg">
                {(item.seller.name || "?").slice(0, 1)}
              </div>
              <div className="flex-1">
                <div className="font-medium">{item.seller.name}</div>
                <div className="text-sm text-muted-foreground line-clamp-1">
                  {item.seller.bio || t("item.seller_default_bio")}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={onMessage} data-testid="seller-card-message">
                {t("item.contact_seller")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, value }) => (
  <div className="rounded-xl bg-muted/50 px-4 py-3">
    <div className="text-xs text-muted-foreground overline">{label}</div>
    <div className="mt-0.5 font-medium">{value}</div>
  </div>
);
const Perk = ({ icon, text }) => (
  <div className="flex items-center gap-2 text-sm text-muted-foreground">
    <span className="w-8 h-8 rounded-full bg-accent flex items-center justify-center text-primary">{icon}</span>
    {text}
  </div>
);
